import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT } from '@ld.consumer-portal/models';
import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { PlatformService } from './platform.service';

export enum SeverityLevel {
  Verbose = 0,
  Information = 1,
  Warning = 2,
  Error = 3,
  Critical = 4,
}

export interface WebError {
  status: number;
  title: string;
  detail: string;
  instance: string;
  type: string;
}

export interface TelemetryProperties {
  [key: string]: string | number | boolean | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class LoggingService {
  private readonly env = inject(ENVIRONMENT);
  private readonly platformService = inject(PlatformService);

  private appInsights: ApplicationInsights | null = null;

  private isInitialized = false;

  private get instrumentationKey(): string {
    return this.env.appSettings.instrumentationKey || '';
  }

  private get isEnabled(): boolean {
    return !!this.instrumentationKey;
  }

  initialize(): void {
    if (this.isInitialized || !this.isEnabled) {
      if (!this.isEnabled) {
        if (!this.env.production) {
          // eslint-disable-next-line no-console -- LoggingService is the canonical console wrapper
          console.warn('Logging: Application Insights instrumentation key not configured');
        }
      }
      return;
    }

    try {
      this.appInsights = new ApplicationInsights({
        config: {
          instrumentationKey: this.instrumentationKey,
          enableAutoRouteTracking: true,
          disableTelemetry: false,
          enableCorsCorrelation: true,
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
          correlationHeaderDomains: ['*.loandepotdev.works', '*.loandepot.com', '*.b2clogin.com'],
        },
      });

      this.appInsights.loadAppInsights();

      this.appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => {
        if (envelope.tags) {
          envelope.tags['ai.operation.name'] = window?.location?.host + window?.location?.pathname;
        }
      });

      this.isInitialized = true;
      if (!this.env.production) {
        // eslint-disable-next-line no-console -- LoggingService is the canonical console wrapper
        console.log('Logging: Application Insights initialized');
      }
    } catch (error) {
      // eslint-disable-next-line no-console -- LoggingService is the canonical console wrapper
      console.error('Logging: Failed to initialize Application Insights', error);
    }
  }

  private constructMessage(message: string): string {
    try {
      const oidcToken = localStorage.getItem('OIDC_AUTH_TOKEN');
      const oidcObj = oidcToken ? JSON.parse(oidcToken) : null;

      let loanNumber: string | null = null;
      try {
        const isHelocStr = localStorage.getItem('isHeloc');
        const isHeloc = isHelocStr ? JSON.parse(isHelocStr) === true : false;

        if (isHeloc) {
          const helocNumber = localStorage.getItem('helocLoanNumber');
          loanNumber = helocNumber ? JSON.parse(helocNumber) : null;
        } else {
          const storedLoanNumber = localStorage.getItem('loanNumber');
          loanNumber = storedLoanNumber ? JSON.parse(storedLoanNumber) : null;
        }
      } catch {
        loanNumber = localStorage.getItem('loanNumber');
      }

      const globalUserId = oidcObj?.profile?.sub || '';
      const deviceInfo = this.platformService.getDeviceInfoString();

      const parts = ['UI:'];
      if (loanNumber) {
        parts.push(`Loan number: ${loanNumber} |`);
      }
      parts.push(message);
      parts.push(`| GlobalUserId: ${globalUserId}`);
      parts.push(`| Device: ${deviceInfo}`);

      return parts.join(' ');
    } catch {
      return `UI: ${message}`;
    }
  }

  private normalizeToError(error: unknown): Error {
    if (error instanceof Error) {
      return error;
    }

    if (typeof error === 'string') {
      return new Error(error);
    }

    try {
      return new Error(JSON.stringify(error));
    } catch {
      return new Error(String(error));
    }
  }

  private combineContextAndMessage(context: string, message: string): string {
    const trimmed = context.trim();
    if (!trimmed) {
      return message;
    }

    const separator = trimmed.endsWith(':') ? ' ' : ': ';
    return `${trimmed}${separator}${message}`;
  }

  logPageView(name?: string, url?: string): void {
    if (!this.isInitialized) return;

    this.appInsights?.trackPageView({
      name,
      uri: url || window.location.href,
    });
  }

  logEvent(name: string, properties?: TelemetryProperties): void {
    if (!this.isInitialized) return;

    this.appInsights?.trackEvent({ name }, properties);

    if (!this.env.production) {
      // eslint-disable-next-line no-console -- LoggingService is the canonical console wrapper
      console.log(`[Event] ${name}`, properties);
    }
  }

  logMetric(name: string, average: number, properties?: TelemetryProperties): void {
    if (!this.isInitialized) return;

    this.appInsights?.trackMetric({ name, average }, properties);
  }

  logException(exception: Error, severityLevel?: SeverityLevel): void;
  logException(exception: unknown, context: string, severityLevel?: SeverityLevel): void;
  logException(
    exception: unknown,
    arg2: SeverityLevel | string = SeverityLevel.Error,
    severityLevel: SeverityLevel = SeverityLevel.Error,
  ): void {
    const context = typeof arg2 === 'string' ? arg2 : undefined;
    const level = typeof arg2 === 'string' ? severityLevel : arg2;

    const normalizedError = this.normalizeToError(exception);

    const message = context
      ? this.combineContextAndMessage(context, normalizedError.message)
      : normalizedError.message;
    const enrichedMessage = message ? this.constructMessage(message) : '';

    // eslint-disable-next-line no-console -- LoggingService is the canonical console wrapper
    console.error(`[Exception] ${enrichedMessage}`, normalizedError);

    if (!this.isInitialized) return;

    this.appInsights?.trackException({
      exception: normalizedError,
      severityLevel: level,
      properties: { enrichedMessage, context },
    });
  }

  logTrace(message: string, properties?: TelemetryProperties): void {
    const enrichedMessage = this.constructMessage(message);

    if (!this.env.production) {
      // eslint-disable-next-line no-console -- LoggingService is the canonical console wrapper
      console.log(`[Trace] ${enrichedMessage}`, properties);
    }

    if (!this.isInitialized) return;

    this.appInsights?.trackTrace({ message: enrichedMessage }, properties);
  }

  logWarning(message: string, properties?: TelemetryProperties): void {
    const enrichedMessage = this.constructMessage(message);

    if (!this.env.production) {
      // eslint-disable-next-line no-console -- LoggingService is the canonical console wrapper
      console.warn(`[Warning] ${enrichedMessage}`, properties);
    }

    if (!this.isInitialized) return;

    this.appInsights?.trackTrace({ message: enrichedMessage, severityLevel: SeverityLevel.Warning }, properties);
  }

  logInfo(message: string, properties?: TelemetryProperties): void {
    const enrichedMessage = this.constructMessage(message);

    if (!this.env.production) {
      // eslint-disable-next-line no-console -- LoggingService is the canonical console wrapper
      console.info(`[Info] ${enrichedMessage}`, properties);
    }

    if (!this.isInitialized) return;

    this.appInsights?.trackTrace({ message: enrichedMessage, severityLevel: SeverityLevel.Information }, properties);
  }

  getCustomizedError(error: unknown, contextMessage: string): Error {
    if (error instanceof Error) {
      error.message = `${contextMessage}: ${error.message}`;
      return error;
    }

    const newError = new Error(contextMessage);
    if (typeof error === 'string') {
      newError.message = `${contextMessage}: ${error}`;
    }
    return newError;
  }

  setAuthenticatedUser(userId: string): void {
    if (!this.isInitialized) return;

    this.appInsights?.setAuthenticatedUserContext(userId, undefined, true);
  }

  clearAuthenticatedUser(): void {
    if (!this.isInitialized) return;

    this.appInsights?.clearAuthenticatedUserContext();
  }

  flush(): void {
    if (!this.isInitialized) return;

    this.appInsights?.flush();
  }

  logAutopaySetup(
    loanNumber: string,
    frequency: 'monthly' | 'biweekly',
    success: boolean,
    properties?: TelemetryProperties,
  ): void {
    this.logEvent('AutopaySetup', {
      loanNumber,
      frequency,
      success,
      ...properties,
    });
  }

  logAutopayCancellation(loanNumber: string, reason: string, success: boolean): void {
    this.logEvent('AutopayCancellation', {
      loanNumber,
      reason,
      success,
    });
  }

  logAutopayEdit(loanNumber: string, changes: string[], success: boolean): void {
    this.logEvent('AutopayEdit', {
      loanNumber,
      changesApplied: changes.join(', '),
      success,
    });
  }

  logAutopayEligibility(loanNumber: string, isEligible: boolean, eligibilityType: string, reason?: string): void {
    this.logEvent('AutopayEligibilityCheck', {
      loanNumber,
      isEligible,
      eligibilityType,
      ineligibilityReason: reason,
    });
  }

  logAutopayError(
    operation: 'setup' | 'edit' | 'cancel' | 'fetch',
    loanNumber: string,
    error: Error | string,
    additionalContext?: TelemetryProperties,
  ): void {
    const errorMessage = error instanceof Error ? error.message : error;

    this.logException(error, 'AutopayError', SeverityLevel.Error);

    this.logEvent('AutopayError', {
      operation,
      loanNumber,
      errorMessage,
      ...additionalContext,
    });
  }

  logAutopayMfaAttempt(
    loanNumber: string,
    method: 'email' | 'sms' | 'call',
    success: boolean,
    attemptNumber?: number,
  ): void {
    this.logEvent('AutopayMfaAttempt', {
      loanNumber,
      verificationMethod: method,
      success,
      attemptNumber,
    });
  }
}
